import { useEffect, useState, type ReactNode } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Moon, Sun, Monitor, LogOut, Zap, ChevronDown, ChevronRight, ChevronsLeft, ChevronsRight, Building2, LogIn } from 'lucide-react';
import { useAuth } from '../../lib/auth-context';
import { useTheme } from '../../lib/theme';
import { useIsBuiStaff } from '../../lib/use-bui-staff';
import { tenantApi, usersApi } from '../../lib/api-client';
import { PRIMARY_NAV_ITEMS, SECONDARY_NAV_ITEMS, ADVANCED_NAV_ITEMS, visibleNavItems, type NavItem } from './nav';
import { can } from '../../lib/rbac';
import { cn } from '../../lib/cn';
import { Button } from '../ui/button';

/** BUI Super-Admin Console's own nav entry — deliberately NOT part of `PRIMARY_NAV_ITEMS` in
 * `nav.tsx`, since visibility here is gated by tenant IDENTITY (`useIsBuiStaff`), not the
 * `requires: Action` permission matrix every other item uses. */
const ADMIN_CONSOLE_ITEM: NavItem = { to: '/admin', label: 'Admin Console', icon: Building2 };

/** BUI Brandline logo, official asset CDN (assets.bui.tools) — swapped by resolved theme so the
 * dark inverse mark stays legible against the sidebar's dark background. */
const BUI_LOGO_URL = { light: 'https://assets.bui.tools/bui-primary-on-white.svg', dark: 'https://assets.bui.tools/bui-inverse.png' };

function BuiBrandBadge() {
  const { resolved } = useTheme();
  const [failed, setFailed] = useState(false);
  if (failed) return null;
  return (
    <div className="flex items-center gap-1.5 px-3 pb-1 pt-2 text-[10px] uppercase tracking-wide text-ink-faint">
      <span>Powered by</span>
      <img src={BUI_LOGO_URL[resolved]} alt="BUI" className="h-3 w-auto" onError={() => setFailed(true)} />
    </div>
  );
}

function getInitials(label: string): string {
  const words = label.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return '?';
  if (words.length === 1) return words[0]!.slice(0, 2).toUpperCase();
  return (words[0]![0]! + words[1]![0]!).toUpperCase();
}

/**
 * Real signed-in user identity (Section 8 of the mockup brief): initials avatar + name +
 * company/role, fetched from GET /v1/tenants/me + GET /v1/users (both auth-only, no specific RBAC
 * permission — safe for every role, see apps/api/src/routes/{tenants,users}.ts). Falls back to the
 * bare role from the JWT principal while those requests are in flight or if the member record
 * can't be found, so the sidebar never shows a blank/broken identity.
 */
function UserIdentity({ expanded }: { expanded: boolean }) {
  const { principal } = useAuth();
  const tenantQuery = useQuery({ queryKey: ['tenant', 'me'], queryFn: () => tenantApi.me() });
  const usersQuery = useQuery({ queryKey: ['users'], queryFn: () => usersApi.list() });

  const member = usersQuery.data?.users.find((u) => u.userId === principal?.userId);
  const isLoading = tenantQuery.isLoading || usersQuery.isLoading;
  const displayName = member?.name ?? member?.email ?? (principal?.role ? `${principal.role} user` : 'Account');
  const role = member?.role ?? principal?.role;
  const company = tenantQuery.data?.tenant.name;
  const subtitle = [company, role].filter(Boolean).join(' · ');

  return (
    <NavLink
      to="/account"
      title={expanded ? undefined : displayName}
      aria-label="Account"
      className={cn(
        'flex items-center gap-2 rounded-xl px-3 py-2 text-left transition-colors hover:bg-surface',
        !expanded && 'justify-center px-0',
      )}
    >
      <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-brand-subtle text-xs font-semibold text-brand-hover">
        {isLoading ? '…' : getInitials(displayName)}
      </span>
      {expanded ? (
        <>
          <span className="min-w-0 flex-1">
            <span className="block truncate text-sm font-medium text-ink">{isLoading ? 'Loading…' : displayName}</span>
            {subtitle ? <span className="block truncate text-xs capitalize text-ink-faint">{subtitle}</span> : null}
          </span>
          <ChevronRight size={14} className="shrink-0 text-ink-faint" />
        </>
      ) : null}
    </NavLink>
  );
}

function ThemeToggle() {
  const { preference, setPreference } = useTheme();
  const options: Array<{ value: 'light' | 'dark' | 'system'; icon: typeof Sun; label: string }> = [
    { value: 'light', icon: Sun, label: 'Light' },
    { value: 'dark', icon: Moon, label: 'Dark' },
    { value: 'system', icon: Monitor, label: 'System' },
  ];
  return (
    <div className="flex items-center gap-0.5 rounded-xl bg-surface-sunken p-0.5">
      {options.map(({ value, icon: Icon, label }) => (
        <button
          key={value}
          type="button"
          aria-label={`${label} theme`}
          aria-pressed={preference === value}
          onClick={() => setPreference(value)}
          className={cn(
            'rounded-lg p-1.5 text-ink-muted transition-colors',
            preference === value && 'bg-surface-raised text-ink shadow-sm',
          )}
        >
          <Icon size={15} />
        </button>
      ))}
    </div>
  );
}

/**
 * Selected state (BUI Dual-Theme brief, Section 11): a raised surface + a 2px orange left
 * indicator + an orange-tinted icon + primary (not orange) text — orange marks "this is where you
 * are," it doesn't recolor the label. Hover is a neutral surface in both states, never orange.
 */
function navLinkClassName({ isActive }: { isActive: boolean }): string {
  return cn(
    'flex items-center gap-3 rounded-xl border-l-2 border-transparent px-3 py-2 text-sm font-medium text-ink-muted transition-colors hover:bg-surface hover:text-ink',
    isActive && 'border-brand bg-surface text-ink hover:bg-surface',
  );
}

function NavItemLink({ item, dimmed, expanded }: { item: NavItem; dimmed?: boolean; expanded: boolean }) {
  return (
    <NavLink
      to={item.to}
      end={item.to === '/'}
      aria-label={item.label}
      title={expanded ? undefined : item.label}
      className={(state) => cn(navLinkClassName(state), !expanded && 'justify-center px-0', dimmed && 'opacity-60')}
    >
      {({ isActive }) => (
        <>
          <item.icon size={17} className={cn('shrink-0', isActive && 'text-brand')} />
          {expanded ? <span className="truncate">{item.label}</span> : null}
        </>
      )}
    </NavLink>
  );
}

/** Secondary nav tier (Attack Paths, Audit Log, Users & Workspace, Kill Switch) — a power user
 * still reaches these often, but they shouldn't compete visually with the 5-item primary tier a
 * non-technical user sees first. Smaller text/icon, no active left-border accent, always visible
 * (not collapsible — that's what distinguishes it from the Advanced section below). */
function SecondaryNavItemLink({ item, expanded }: { item: NavItem; expanded: boolean }) {
  return (
    <NavLink
      to={item.to}
      aria-label={item.label}
      title={expanded ? undefined : item.label}
      className={({ isActive }) =>
        cn(
          'flex items-center gap-3 rounded-lg px-3 py-1.5 text-xs font-medium text-ink-faint transition-colors hover:bg-surface hover:text-ink-muted',
          isActive && 'text-ink-muted',
          !expanded && 'justify-center px-0',
        )
      }
    >
      <item.icon size={15} className="shrink-0" />
      {expanded ? <span className="truncate">{item.label}</span> : null}
    </NavLink>
  );
}

const ADVANCED_OPEN_STORAGE_KEY = 'defendiq_nav_advanced_open';

function readAdvancedOpen(): boolean {
  try {
    return window.localStorage.getItem(ADVANCED_OPEN_STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
}

function writeAdvancedOpen(open: boolean): void {
  try {
    window.localStorage.setItem(ADVANCED_OPEN_STORAGE_KEY, open ? 'true' : 'false');
  } catch {
    // localStorage unavailable (e.g. private mode) — fail closed to in-memory state only.
  }
}

function AdvancedSection({ items, expanded }: { items: NavItem[]; expanded: boolean }) {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    setOpen(readAdvancedOpen());
  }, []);

  if (items.length === 0) return null;

  const toggle = () => {
    setOpen((v) => {
      const next = !v;
      writeAdvancedOpen(next);
      return next;
    });
  };

  return (
    <div className="mt-2 border-t border-border pt-2">
      <button
        type="button"
        onClick={toggle}
        aria-expanded={open}
        title={expanded ? undefined : 'Advanced'}
        className={cn(
          'flex w-full items-center gap-2 rounded-xl px-3 py-2 text-xs font-semibold uppercase tracking-wide text-ink-faint transition-colors hover:bg-surface hover:text-ink-muted',
          !expanded && 'justify-center px-0',
        )}
      >
        {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        {expanded ? 'Advanced' : null}
      </button>
      {open ? (
        <div className="space-y-0.5">
          {items.map((item) => (
            <NavItemLink key={item.to} item={item} dimmed expanded={expanded} />
          ))}
        </div>
      ) : null}
    </div>
  );
}

const SIDEBAR_EXPANDED_STORAGE_KEY = 'defendiq_sidebar_expanded';

function readSidebarExpanded(): boolean {
  try {
    const stored = window.localStorage.getItem(SIDEBAR_EXPANDED_STORAGE_KEY);
    return stored === null ? true : stored === 'true';
  } catch {
    return true;
  }
}

function writeSidebarExpanded(expanded: boolean): void {
  try {
    window.localStorage.setItem(SIDEBAR_EXPANDED_STORAGE_KEY, expanded ? 'true' : 'false');
  } catch {
    // localStorage unavailable (e.g. private mode) — fail closed to in-memory state only.
  }
}

export function AppShell({ children }: { children: ReactNode }) {
  const { principal, logout, impersonating, exitImpersonation } = useAuth();
  const isBuiStaff = useIsBuiStaff();
  const navigate = useNavigate();
  const primaryItems = visibleNavItems(PRIMARY_NAV_ITEMS, principal?.role);
  const secondaryItems = visibleNavItems(SECONDARY_NAV_ITEMS, principal?.role);
  const advancedItems = visibleNavItems(ADVANCED_NAV_ITEMS, principal?.role);
  const canQuickScan = principal?.role === undefined || can(principal.role, 'authorization:activate');
  const [expanded, setExpanded] = useState(true);

  useEffect(() => {
    setExpanded(readSidebarExpanded());
  }, []);

  const toggleExpanded = () => {
    setExpanded((v) => {
      const next = !v;
      writeSidebarExpanded(next);
      return next;
    });
  };

  const handleLogout = () => {
    logout();
    navigate('/login', { replace: true });
  };

  const handleExitImpersonation = () => {
    exitImpersonation();
    navigate('/admin', { replace: true });
  };

  return (
    <div className="flex min-h-screen flex-col">
      {impersonating ? (
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-warning/30 bg-warning-soft px-4 py-2 text-sm text-warning">
          <span className="font-medium">
            <span className="font-semibold">[DefendIQ Admin]</span> Viewing as: {impersonating.workspaceName}
          </span>
          <button
            type="button"
            onClick={handleExitImpersonation}
            className="inline-flex items-center gap-1.5 rounded-lg px-2 py-1 font-semibold underline-offset-2 hover:underline"
          >
            <LogIn size={14} />
            Exit to Admin Console
          </button>
        </div>
      ) : null}
      <div className="flex flex-1">
      <aside
        className={cn(
          'flex shrink-0 flex-col border-r border-border bg-sidebar transition-[width]',
          expanded ? 'w-60' : 'w-18',
        )}
      >
        <div className={cn('flex items-center gap-2 px-5 py-5', !expanded && 'justify-center px-0')}>
          <img src="/defendiq-emblem.png" alt="DefendIQ" width={28} height={28} className="shrink-0 object-contain" />
          {expanded ? (
            <span className="truncate text-base font-semibold text-ink">
              Defend<span className="text-brand">IQ</span>
            </span>
          ) : null}
        </div>
        {canQuickScan ? (
          <div className={cn('px-3 pb-3', !expanded && 'px-2')}>
            <Button
              asChild
              className={cn('h-[42px] w-full justify-center gap-2 bg-brand text-white hover:bg-brand-hover', !expanded && 'px-0')}
            >
              <NavLink to="/quick-scan" title={expanded ? undefined : 'New Scan'} aria-label="New Scan">
                <Zap size={16} />
                {expanded ? 'New Scan' : null}
              </NavLink>
            </Button>
          </div>
        ) : null}
        <nav className={cn('flex-1 space-y-0.5 overflow-y-auto px-3', !expanded && 'px-2')}>
          {isBuiStaff ? <NavItemLink item={ADMIN_CONSOLE_ITEM} expanded={expanded} /> : null}
          {primaryItems.map((item) => (
            <NavItemLink key={item.to} item={item} expanded={expanded} />
          ))}
          {secondaryItems.length > 0 ? (
            <div className="mt-2 space-y-0.5 border-t border-border pt-2">
              {secondaryItems.map((item) => (
                <SecondaryNavItemLink key={item.to} item={item} expanded={expanded} />
              ))}
            </div>
          ) : null}
          <AdvancedSection items={advancedItems} expanded={expanded} />
        </nav>
        <div className={cn('space-y-2 border-t border-border p-3', !expanded && 'px-2')}>
          {expanded ? (
            <ThemeToggle />
          ) : (
            <div className="flex justify-center">
              <ThemeToggle />
            </div>
          )}
          <UserIdentity expanded={expanded} />
          <Button
            variant="ghost"
            size="sm"
            title={expanded ? undefined : 'Sign out'}
            aria-label="Sign out"
            className={cn('w-full justify-start gap-2', !expanded && 'justify-center px-0')}
            onClick={handleLogout}
          >
            <LogOut size={15} />
            {expanded ? 'Sign out' : null}
          </Button>
          <button
            type="button"
            onClick={toggleExpanded}
            aria-label={expanded ? 'Collapse sidebar' : 'Expand sidebar'}
            className={cn(
              'flex w-full items-center gap-2 rounded-xl px-3 py-2 text-xs font-medium text-ink-faint transition-colors hover:bg-surface hover:text-ink-muted',
              !expanded && 'justify-center px-0',
            )}
          >
            {expanded ? <ChevronsLeft size={15} /> : <ChevronsRight size={15} />}
            {expanded ? 'Collapse' : null}
          </button>
          {expanded ? <BuiBrandBadge /> : null}
        </div>
      </aside>
      <main className="flex-1 overflow-y-auto bg-surface-sunken p-8">{children}</main>
      </div>
    </div>
  );
}
